import { Link } from '@inertiajs/react'
import { motion } from 'framer-motion'
import { useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { PublicLayout } from '@/components/PublicLayout'
import { AnimatedLandingHero } from '@/components/AnimatedLandingHero'

// Cache-busting: update this version when you change images
const IMAGE_VERSION = 'v2'

// Helper to add cache-busting query param to image URLs
const withCacheBust = (path: string) => `${path}?${IMAGE_VERSION}`

import { 
  BookOpen, 
  Brain, 
  FlaskConical, 
  Mail, 
  ChevronRight,
  PenTool,
  Lightbulb,
  Compass,
  Search,
  Rocket
} from 'lucide-react'

interface LandingProps {
  message: string
  user?: {
    id: number
    name: string
    email: string
    username?: string
    role?: 'user' | 'admin'
  }
  featuredPosts: {
    id: number
    title: string
    slug: string
    summary: string
    publishedAt?: string
  }[]
}

export default function Landing({ message, user, featuredPosts }: LandingProps) {
  //console.log('Landing component rendering', { message, user, featuredPosts })
  
  // Ref for Awaken section
  const awakenRef = useRef<HTMLElement>(null)
  
  return (
    <PublicLayout user={user}>
      <main className="flex-1">
        {/* Hero Section - Refactored into separate component */}
        <AnimatedLandingHero user={user} />

        <Separator className="container mx-auto" />

        {/* Awaken the Craftsman Within */}
        <section 
          id="awaken"
          className="py-20 lg:py-60 bg-background relative overflow-hidden"
          ref={awakenRef}
        >
          {/* Background image - mobile only (behind text) */}
          <motion.div 
            className="absolute inset-0 z-0 lg:hidden"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ amount: 0.25, once: true }}
            transition={{ duration: 5, delay: 1, ease: "easeOut" }}
            style={{
              backgroundImage: `url(${withCacheBust('/images/man-network.webp')})`,
              backgroundSize: 'contain',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          />
          {/* Dark overlay for mobile image */}
          <div className="absolute inset-0 z-0 lg:hidden bg-background/70" />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-stretch">
              {/* Left side - Text content */}
              <motion.div 
                className="max-w-3xl mx-auto text-center lg:text-left lg:mx-0 flex flex-col justify-center py-6 lg:py-12"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ amount: 0.25, once: true }}
                transition={{ duration: 1 }}
              >
                <h2 className="text-3xl md:text-4xl font-semibold text-white lg:text-foreground mb-6 flex items-center justify-center lg:justify-start gap-3">
                  <PenTool className="w-8 h-8 text-orange-400 flex-shrink-0" />
                  Awaken the Craftsman Within
                </h2>
                <p className="text-white/90 lg:text-muted-foreground text-lg leading-relaxed">
                  Behind every line of code lies clarity, creativity, and intention. 
                  This space is dedicated to exploring how deep technical knowledge and 
                  mindful practice can transform how we build with AI. At MantraVid We see code not 
                  merely as mechanics, but as a form of consciousness exploration, a 
                  craft where precision meets poetry.
                </p>
              </motion.div>
              
              {/* Right side - Image with scroll effect - desktop only */}
              <div className="relative h-full lg:min-h-0 hidden lg:block">
                <motion.div 
                  className="absolute inset-0 z-0 rounded-lg overflow-hidden"
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ amount: 0.25, once: true }}
                  transition={{ 
                    duration: 5, 
                    delay: 1,
                    ease: "easeOut" 
                  }}
                  style={{
                    backgroundImage: `url(${withCacheBust('/images/man-network.webp')})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                />
                {/* Dark overlay on right side */}
                <div className="absolute inset-0 z-0 bg-gradient-to-l from-background/30 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </section>

        {/* What You'll Find Here */}
        <section 
          id="features"
          className="py-20 bg-muted/20 relative overflow-hidden"
        >
          {/* Subtle background image */}
          <div 
            className="absolute inset-0 z-0 opacity-10"
            style={{
              backgroundImage: `url(${withCacheBust('/images/planet-moons.webp')})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          />
          <div className="container mx-auto px-4 relative z-10">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 flex items-center justify-center gap-3">
                <Search className="w-8 h-8 text-purple-500 flex-shrink-0" />
                What You'll Find Here
              </h2>
              <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                A curated collection of insights for the mindful technologist
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {/* Feature 1 */}
              <Card className="group hover:border-primary/30 transition-colors">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-orange-400/10 group-hover:bg-orange-400/20 transition-colors">
                      <BookOpen className="w-5 h-5 text-orange-400 group-hover:text-orange-300 transition-colors" />
                    </div>
                    <CardTitle className="text-xl">Exclusive Articles</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    Advanced posts and reflections on AI, consciousness, and building 
                    intelligent systems. Mantravid Member only content for those who seek deeper understanding.
                  </CardDescription>
                </CardContent>
              </Card>

              {/* Feature 2 */}
              <Card className="group hover:border-primary/30 transition-colors">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-purple-500/10 group-hover:bg-purple-500/20 transition-colors">
                      <Brain className="w-5 h-5 text-purple-500 group-hover:text-purple-400 transition-colors" />
                    </div>
                    <CardTitle className="text-xl">AI & Awareness</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    Essays exploring both the technical and philosophical sides of 
                    intelligent systems. We examine the intersection where code meets consciousness.
                  </CardDescription>
                </CardContent>
              </Card>

              {/* Feature 3 */}
              <Card className="group hover:border-primary/30 transition-colors">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                      <FlaskConical className="w-5 h-5 text-emerald-500 group-hover:text-emerald-400 transition-colors" />
                    </div>
                    <CardTitle className="text-xl">Research & Practice</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    Guides, news, implementations, and meditations on building 
                    consciously with AI. Practical wisdom for the hands on builder.
                  </CardDescription>
                </CardContent>
              </Card>

              {/* Feature 4 */}
              <Card className="group hover:border-primary/30 transition-colors">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-cyan-500/10 group-hover:bg-cyan-500/20 transition-colors">
                      <Mail className="w-5 h-5 text-cyan-500 group-hover:text-cyan-400 transition-colors" />
                    </div>
                    <CardTitle className="text-xl">Future Newsletters</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    A space for curated insights, delivered to your inbox. 
                    Coming soon join the journey to be notified when we launch.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        <Separator className="container mx-auto" />

        {/* For the Mindful Technologist */}
        <section 
          id="mindful"
          className="py-20 bg-background relative overflow-hidden"
        >
          {/* Background image */}
          <motion.div 
            className="absolute right-0 top-0 h-full w-1/2 z-0 hidden md:block"
            style={{
              backgroundImage: `url(${withCacheBust('/images/two-minds.webp')})`,
              backgroundSize: 'contain',
              backgroundPosition: 'right center',
              backgroundRepeat: 'no-repeat',
            }}
            animate={{
              opacity: [0.50, 0.88, 0.50],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-6 flex items-center justify-center gap-3">
                <Compass className="w-8 h-8 text-purple-500 flex-shrink-0" />
                For the Mindful Technologist
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8">
                We're creating a home for those who see agentic coding as more than mechanics 
                as a form of consciousness exploration. Whether you read as a guest or 
                join as a member, you'll find depth, reflection, and clarity in every article.
                This is a space for those who believe that building technology thoughtfully 
                is itself a practice of awareness.
              </p>
              <Button variant="outline" size="lg" asChild>
                <Link href="/blog">
                  <Lightbulb className="mr-2 w-4 h-4" />
                  Read the Latest Articles
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Join the Journey */}
        <section 
          id="join"
          className="py-32 relative overflow-hidden min-h-[600px] flex items-center"
        >
          {/* Background image */}
          <div 
            className="absolute inset-0 z-0"
            style={{
              backgroundImage: `url(${withCacheBust('/images/man-horizon.webp')})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat'
            }}
          />
          {/* Overlay */}
          <div className="absolute inset-0 z-0 bg-linear-to-b from-background/80 via-background/70 to-background" />
          <div className="container mx-auto px-4 text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 flex items-center justify-center gap-3">
              <Rocket className="w-8 h-8 text-cyan-500 shrink-0" />
              Join the Journey
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-10">
              Sign up for free access to member-only posts and stay updated as we grow. 
              Your awareness is our algorithm.
            </p>
            {user ? (
              <Button size="lg" asChild className="bg-primary hover:bg-primary/90">
                <Link href="/dashboard">
                  Go to Your Dashboard
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            ) : (
              <Button size="lg" asChild className="bg-primary hover:bg-primary/90">
                <Link href="/sign_up">
                  Create Free Account
                  <ChevronRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
            )}
          </div>
        </section>
      </main>
    </PublicLayout>
  )
}
